/**
 * Customer API client aligned with old version patterns
 */

import { CUSTOMER_API_BASE_URL } from '../config/env';
import type { UserRole, ProfileData } from './user.service';
import { s3CredentialsManager } from './s3CredentialsManager.service';

import type { 
  Scan,
  ScanMetadata,
  CreateScanDto,
  UpdateScanDto,
  CreateScanMetadataDto,
  UpdateScanMetadataDto,
  ScanFilterDto,
  TriggerReconstructionDto,
  ScanCreationResponse,
  ApiResponse,
  ScanVersion,
  CreateScanVersionDto,
  ReconstructionStatusResponse,
  UpdateScanVersionDto
} from '../types/scan.types';
import { ScanStatus } from '../types/scan.types';

export interface RegisterRequest {
  token: string;
  email: string;
}

export interface RegisterResponse {
  success: boolean;
  data: {
    user: {
      userId: string;
      email: string;
      role: UserRole;
      profileId?: string;
      sharedScanIds?: string[];
      lastLoginAt: string;
      createdAt: string;
      updatedAt: string;
    };
    whitelisted: boolean;
    role: UserRole;
    needsProfile: boolean;
  };
}

// Using ProfileData type from user.service to ensure consistency

export interface WhitelistEntry {
  _id?: string;
  email: string;
  role: string;
  addedBy: string;
  addedAt: string;
  invitationSent: boolean;
}

export interface User {
  _id: string;
  userId: string;
  email: string;
  role: UserRole;
  profileId?: string;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string;
  name?: string;
  id?: string;
}

export interface GausModel {
  Key: string;
  Size: number;
  LastModified: string;
  signedUrl: string;
  fileName: string;
  fileType: string;
}

export interface GausModelsResponse {
  success: boolean;
  data: {
    objects: GausModel[];
    keyCount: number;
    nextContinuationToken?: string;
    isTruncated?: boolean;
  };
}

export interface GausCredentialsResponse {
  success: boolean;
  data: {
    credentials: {
      AccessKeyId: string;
      SecretAccessKey: string;
      SessionToken: string;
      Expiration: string;
    };
    region: string;
    bucket: string;
  };
}

export interface GausModelUrlResponse {
  success: boolean;
  data: {
    signedUrl: string;
    objectKey: string;
    expiresIn: number;
  };
}

export interface GausLibraryStatusResponse {
  success: boolean;
  data: {
    success: boolean;
    message: string;
    objectCount?: number;
    region: string;
    bucket: string;
    sampleObjects?: Array<{
      key: string;
      size: number;
      hasSignedUrl: boolean;
    }>;
  };
}

// Sharing interfaces
export interface ShareScanRequest {
  email: string;
  scanVersionId?: string;
  message?: string;
}

export interface ShareScanResponse {
  success: boolean;
  message: string;
  data: {
    shareId: string;
    email: string;
    scanId: string;
    scanVersionId?: string;
    sharedBy: string;
    sharedAt: Date;
    status: 'pending' | 'accepted' | 'revoked';
    invitationSent: boolean;
    message?: string;
  };
}

export interface ShareListItem {
  shareId: string;
  email: string;
  scanId: string;
  scanName: string;
  scanVersionId?: string;
  sharedBy: string;
  sharedByName?: string;
  sharedAt: Date;
  status: 'pending' | 'accepted' | 'revoked';
  invitationSent: boolean;
  invitationSentAt?: Date;
  message?: string;
}

export interface GetSharesResponse {
  success: boolean;
  data: {
    shares: ShareListItem[];
    total: number;
    hasMore: boolean;
  };
}

interface SupportTicketRequest {
  subject: string;
  message: string;
  userEmail: string;
  userName: string;
}

interface SupportTicketResponse {
  success: boolean;
  data: {
    ticketId: string;
    status: string;
    createdAt: string;
    subject: string;
  };
  message: string;
}

interface FeedbackRequest {
  message: string;
  userEmail: string;
  userName: string;
}

interface FeedbackResponse {
  success: boolean;
  data?: {
    feedbackId?: string;
    status?: string;
    createdAt?: string;
  };
  message: string;
}

export interface TeamEmailRequest {
  to: string;
  subject: string;
  message: string;
}

export interface TeamEmailResponse {
  success: boolean;
  message: string;
  data?: {
    to: string;
    from: { name: string; email: string };
    cc: string[];
    replyTo: string;
  };
}

interface EnterpriseInquiryRequest {
  name: string;
  email: string;
  company: string;
  message: string;
  /* 'sdk' is accepted here and sent, but ydrive-customer normalises anything
     that is not 'desktop-app' to 'spatial-model' (support.controller.ts:2221),
     so an SDK enquiry is filed under spatial-model until that line is updated. */
  source?: 'spatial-model' | 'desktop-app' | 'sdk';
}

interface EnterpriseInquiryResponse {
  success: boolean;
  data?: {
    inquiryId?: string;
    status?: string;
    submittedAt?: string;
    company?: string;
  };
  message: string;
}

export interface WaitlistProfileData {
  email: string;
  fullName: string;
  studioName: string;
  jobTitle?: string;
  phoneNumber?: string;
  productionType?: string;
  project?: string;
  softwareTools?: string[];
  exportFormat?: string;
}

export interface CreateWaitlistProfileRequest {
  email: string;
  fullName: string;
  studioName: string;
  jobTitle?: string;
  phoneNumber?: string; //make phone no. optional
  project: string;
  // Optional fields from your form
  productionType?: string;
  softwareTools?: string[];
  exportFormat?: string;
}

export interface WaitlistProfileResponse {
  success: boolean;
  data: WaitlistProfileData;
  message?: string;
}

export interface CheckWaitlistProfileResponse {
  success: boolean;
  exists: boolean;
  message: string;
}

export interface GeoAutomationScene {
  sceneId: string;
  category: string;
  sceneName: string;
  location: string;
  createdAt: string;
  envMap?: string;
  imagePoses?: string;
  manifest: string;
 }

 export interface GeoAutomationResponse {
  success: boolean;
  data: GeoAutomationScene[];
  count: number;
  filters?: {
    category: string | null;
    location: string | null;
    search: string | null;
  };
}

export interface GeoAutomationSceneResponse {
  success: boolean;
  data: GeoAutomationScene;
}

export interface GeoAutomationCategoriesResponse {
  success: boolean;
  data: string[];
  count: number;
}

export interface GeoAutomationSearchResponse {
  success: boolean;
  data: GeoAutomationScene[];
  count: number;
  searchTerm: string;
}

export interface GeoAutomationPOI {
  id: string;
  publicId?: number;                                    
  name: string;
  type: 'POINT' | 'LINESTRING' | 'POLYGON';
  latitude: number;
  longitude: number;
  altitude: number;
  layerName?: string;
  properties?: Record<string, string>;
  comments?: string | null;
  points?: Array<{ x: number; y: number; z: number }>; //  MTM8 EPSG:2950
}

export interface GeoAutomationPOIsResponse {
  success: boolean;
  data: GeoAutomationPOI[];
  count: number;
  sceneId: string;
  projectId: string;
  warning?: string;
}

export interface ImageUrlResult {
  imageName: string;
  url:       string;
  expiresAt: string;
  error?:    string;
}
 
export interface ImagePresignedUrlsResponse {
  success: boolean;
  data: {
    count: number;
    urls:  ImageUrlResult[];
  };
}

export interface AssetLibraryLabel {
  id: string;
  sceneId: string;
  name: string;
  type: 'POINT';
  source: string;
  coordinateSpace: 'threejs-world';
  geometry: {
    type: 'Point';
    coordinates: [number, number, number];
  };
  world: [number, number, number];
  properties: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface AssetLibraryLabelsResponse {
  success: boolean;
  data: AssetLibraryLabel[];
  count: number;
  sceneId: string;
}

export interface AssetLibraryLabelResponse {
  success: boolean;
  data: AssetLibraryLabel;
}

export interface Collectiv3Scene {
  pinsUrl?: string;
  inspectionUrl?: string;
  sceneId: string;
  category: string;
  credits: string;
  KnowMore: string;
  sceneName: string;
  location: string;
  sceneDescription: string;
  loadingDescription: string;
  cardDescription: string;
  createdAt: string;
  gaus: string;
  audio: string;
  envMap?: string;
  imagePoses?: string;
  thumbnail: string;
  shootTime: string;
  sceneType?: 'aerial' | 'street' | 'indoor' | 'object' | 'outdoor';
  coordinateFrame?: 'zup' | 'yup';
  defaultCameraPos?: [number, number, number];
  defaultLookAt?: [number, number, number];
}

export interface Collectiv3Response {
  success: boolean;
  data: Collectiv3Scene[];
  count: number;
  filters?: {
    category: string | null;
    location: string | null;
    search: string | null;
  };
}

export interface Collectiv3SceneResponse {
  success: boolean;
  data: Collectiv3Scene;
}

export interface Collectiv3CategoriesResponse {
  success: boolean;
  data: string[];
  count: number;
}

export interface Collectiv3SearchResponse {
  success: boolean;
  data: Collectiv3Scene[];
  count: number;
  searchTerm: string;
}

export interface ProgressionScene {
  sceneId: string;
  category: string;
  sceneName: string;
  description: string;
  gaus: string[];
  envMap?: string;
  sceneType: 'aerial' | 'street' | 'indoor' | 'object';
  siSessionNames?: string[];
  siContextNote?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProgressionSceneResponse {
  success: boolean;
  data: ProgressionScene;
}

export interface MonitoringCycleRequest {
  scans?: Array<{
    scanId: string;
    status: string;
    sequenceUuid?: string;
    // Add other scan fields that might be needed
  }>;
}

export interface MonitoringResult {
  totalProcessed: number;
  statusUpdated: number;
  notificationsSent: number;
  errors: Array<{ scanId: string; error: string }>;
  duration: number;
}

export interface MonitoringCycleResponse {
  success: boolean;
  message: string;
  data: MonitoringResult;
}

/**
 * Enhanced API fetch with response validation and proper error handling
 */
async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${CUSTOMER_API_BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include',              // send cookies
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),        // no Authorization header
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    try {
      const errorJson = JSON.parse(errorText);
      throw new Error(errorJson.error || errorJson.message || `HTTP ${response.status}: ${response.statusText}`);
    } catch (parseError) {
      throw new Error(`API call failed [${response.status}]: ${errorText.substring(0, 200)}`);
    }
  }

  try {
    const data = await response.json();
    return data;
  } catch (parseError) {
    throw new Error(`Failed to parse response JSON: ${parseError instanceof Error ? parseError.message : 'Unknown parse error'}`);
  }
}

/**
 * Validate S3 credentials response structure
 */
function validateS3CredentialsResponse(response: any): void {
  if (!response) {
    throw new Error('S3 credentials response is null or undefined');
  }

  // Check for wrapper structure
  if (!response.data) {
    throw new Error('S3 credentials response missing data field');
  }

  const data = response.data;

  // Validate required fields
  const requiredFields = ['credentials', 'uploadId', 'bucket', 'region', 'prefix'];
  for (const field of requiredFields) {
    if (!data[field]) {
      throw new Error(`S3 credentials response missing required field: ${field}`);
    }
  }

  // Validate credentials structure
  const credentials = data.credentials;
  const requiredCredFields = ['AccessKeyId', 'SecretAccessKey', 'SessionToken', 'Expiration'];
  for (const field of requiredCredFields) {
    if (!credentials[field]) {
      throw new Error(`S3 credentials missing required credential field: ${field}`);
    }
  }

  // Validate expiration is not in the past
  const expiration = new Date(credentials.Expiration);
  if (expiration.getTime() <= Date.now()) {
    throw new Error('S3 credentials have already expired');
  }

  console.log(`[CustomerApi] S3 credentials validated successfully. Expires: ${expiration.toISOString()}`);
}

/**
 * Validate upload status response
 */
function validateUploadStatusResponse(response: any): void {
  if (!response) {
    throw new Error('Upload status response is null or undefined');
  }

  if (!response.uploadId) {
    throw new Error('Upload status response missing uploadId');
  }

  if (!response.status) {
    throw new Error('Upload status response missing status field');
  }

  const validStatuses = ['pending', 'in-progress', 'completed', 'failed', 'cancelled'];
  if (!validStatuses.includes(response.status)) {
    throw new Error(`Invalid upload status: ${response.status}. Valid statuses: ${validStatuses.join(', ')}`);
  }

  console.log(`[CustomerApi] Upload status validated: ${response.status} for upload ${response.uploadId}`);
}

/**
 * Customer API service class
 */
class CustomerApiService {
  // User Registration
  async registerUser(token: string, email: string): Promise<RegisterResponse> {
    try {
      const response = await apiFetch<RegisterResponse>('/api/register', {
        method: 'POST',
        body: JSON.stringify({ email })
      });

      if (!response.success || !response.data) {
        throw new Error('Invalid registration response structure');
      }

      console.log(`[CustomerApi] User registered successfully: ${email}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Registration failed for ${email}:`, error);
      throw error;
    }
  }

  // Profile Management with validation
  async createProfile(token: string, profileData: ProfileData): Promise<{ profile: ProfileData; isNewProfile: boolean }> {
    try {
      if (!profileData.fullName || !profileData.email) {
        throw new Error('Profile data missing required fields: fullName, email');
      }

      const response = await apiFetch<{ profile: ProfileData; isNewProfile: boolean }>('/api/profiles', {
        method: 'POST',
        body: JSON.stringify(profileData)
      });

      if (!response.profile) {
        throw new Error('Profile creation response missing profile data');
      }

      console.log(`[CustomerApi] Profile created successfully for: ${profileData.email}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Profile creation failed:`, error);
      throw error;
    }
  }

  async getProfile(token: string): Promise<ProfileData> {
    try {
      const profile = await apiFetch<ProfileData>('/api/profiles');
      
      if (!profile.userId || !profile.email) {
        throw new Error('Profile response missing required fields');
      }

      return profile;
    } catch (error) {
      console.error(`[CustomerApi] Get profile failed:`, error);
      throw error;
    }
  }

  async updateProfile(token: string, profileData: Partial<Omit<ProfileData, 'userId'>>): Promise<ProfileData> {
    try {
      const response = await apiFetch<ProfileData>('/api/profiles', {
        method: 'PATCH',
        body: JSON.stringify(profileData)
      });

      if (!response.userId || !response.email) {
        throw new Error('Updated profile response missing required fields');
      }

      console.log(`[CustomerApi] Profile updated successfully for: ${response.email}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Profile update failed:`, error);
      throw error;
    }
  }

  async hasProfile(token: string): Promise<boolean> {
    try {
      await this.getProfile(token);
      return true;
    } catch (error) {
      return false;
    }
  }

  // User Management (Admin) - aligned with old format
  async getAllUsers(): Promise<User[]> {
    try {
      const response = await apiFetch<{ success: boolean; data: User[] }>('/api/users');
      
      // Handle both old format { success: true, data: [...] } and direct array
      if (response && typeof response === 'object' && 'success' in response && response.success && Array.isArray(response.data)) {
        console.log(`[CustomerApi] Retrieved ${response.data.length} users`);
        return response.data;
      }
      
      // Fallback for direct array response
      const users = Array.isArray(response) ? response : [];
      console.log(`[CustomerApi] Retrieved ${users.length} users (direct array)`);
      return users;
    } catch (error) {
      console.error(`[CustomerApi] Get all users failed:`, error);
      throw error;
    }
  }

  async getUserById(userId: string): Promise<User> {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const user = await apiFetch<User>(`/api/users/${userId}`);
      
      if (!user.userId && !user._id) {
        throw new Error('User response missing ID fields');
      }

      return user;
    } catch (error) {
      console.error(`[CustomerApi] Get user by ID failed for ${userId}:`, error);
      throw error;
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const user = await apiFetch<User>('/api/users/me');
      
      if (!user.userId && !user._id) {
        throw new Error('Current user response missing ID fields');
      }

      return user;
    } catch (error) {
      console.error(`[CustomerApi] Get current user failed:`, error);
      throw error;
    }
  }
  /**
 * Update user role by userId (RESTful approach)
 * Endpoint: PUT /api/users/{userId}/role
 * Security: Protected by adminGuard middleware at controller level
 * @param userId The user ID to update
 * @param role The new role to assign
 * @returns Promise with the updated user data
 */
  async updateUserRole(userId: string, role: UserRole): Promise<{ message: string }> {
    try {
      if (!userId || !role) {
        throw new Error('User ID and role are required');
      }

      const response = await apiFetch<{ message: string }>(`/api/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role })
    });

      console.log(`[CustomerApi] User role updated successfully for ${userId}: ${role}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Update user role failed for ${userId}:`, error);
      throw error;
    }
  }

  // Whitelist Management (Admin) - aligned with old format
  async getAllWhitelistEntries(): Promise<WhitelistEntry[]> {
    try {
      const response = await apiFetch<{ success: boolean; data: WhitelistEntry[] }>('/api/whitelist');
      
      // Handle both old format { success: true, data: [...] } and direct array
      if (response && typeof response === 'object' && 'success' in response && response.success && Array.isArray(response.data)) {
        console.log(`[CustomerApi] Retrieved ${response.data.length} whitelist entries`);
        return response.data;
      }
      
      // Fallback for direct array response
      const entries = Array.isArray(response) ? response : [];
      console.log(`[CustomerApi] Retrieved ${entries.length} whitelist entries (direct array)`);
      return entries;
    } catch (error) {
      console.error(`[CustomerApi] Get whitelist entries failed:`, error);
      throw error;
    }
  }

  async addToWhitelist(entry: Omit<WhitelistEntry, '_id' | 'addedAt'>): Promise<{ message: string }> {
    try {
      if (!entry.email || !entry.role) {
        throw new Error('Whitelist entry missing required fields: email, role');
      }

      const response = await apiFetch<{ message: string }>('/api/whitelist', {
        method: 'POST',
        body: JSON.stringify(entry)
      });

      console.log(`[CustomerApi] Added to whitelist: ${entry.email} as ${entry.role}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Add to whitelist failed for ${entry.email}:`, error);
      throw error;
    }
  }

  async updateWhitelistRole(email: string, role: string): Promise<{ message: string }> {
    try {
      if (!email || !role) {
        throw new Error('Email and role are required for whitelist update');
      }

      // Use old format: send email and role
      const response = await apiFetch<{ message: string }>('/api/whitelist/role', {
        method: 'PUT',
        body: JSON.stringify({ email, role })
      });

      console.log(`[CustomerApi] Whitelist role updated for ${email}: ${role}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Update whitelist role failed for ${email}:`, error);
      throw error;
    }
  }

 
/**
 * Update role for an invited user by email (RESTful approach)
 * This is for users who have been invited but haven't logged in yet (whitelist entries with email only)
 * Endpoint: PUT /api/whitelist/{email}/role
 * Security: Protected by adminGuard middleware at controller level
 * @param email The email of the invited user whose role we want to update
 * @param role The new role to assign to the invited user
 * @param addedBy The admin user making the change (for audit trail)
 * @returns Promise with success message
 */
async updateInvitationRole(email: string, role: UserRole, addedBy?: string): Promise<{ message: string }> {
  try {
    if (!email || !role) {
      throw new Error('Email and role are required');
    }

    console.log(`[CustomerApi] Admin ${addedBy || 'unknown'} updating invitation role for TARGET EMAIL ${email} to ${role}`);
    
    // The endpoint becomes: PUT /api/whitelist/{targetEmail}/role
    // Email needs to be URL encoded for safety (handles special characters like +, @, etc.)
    const encodedEmail = encodeURIComponent(email);
    
    // The admin's authentication is handled via the Authorization header (JWT token)
    const response = await apiFetch<{ message: string }>(`/api/whitelist/${encodedEmail}/role`, {
      method: 'PUT',
      body: JSON.stringify({ 
        role,
        addedBy: addedBy || 'admin' // Include who made the change for audit trail
      })
    });

    console.log(`[CustomerApi] Invitation role updated successfully for ${email} to: ${role} by ${addedBy || 'admin'}`);
    return response;
  } catch (error) {
    console.error(`[CustomerApi] Update invitation role failed for ${email}:`, error);
    throw error;
  }
}

  async removeFromWhitelist(entryId: string): Promise<{ message: string }> {
    try {
      if (!entryId) {
        throw new Error('Entry ID is required for whitelist removal');
      }

      const response = await apiFetch<{ message: string }>(`/api/whitelist/${entryId}`, {
        method: 'DELETE'
      });

      console.log(`[CustomerApi] Removed from whitelist: ${entryId}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Remove from whitelist failed for ${entryId}:`, error);
      throw error;
    }
  }

  async checkWhitelist(email: string): Promise<{ whitelisted: boolean; role?: string }> {
    try {
      if (!email) {
        throw new Error('Email is required for whitelist check');
      }

      const response = await apiFetch<{ whitelisted: boolean; role?: string }>(
      `/api/whitelist/check/${encodeURIComponent(email)}`);

      if (typeof response.whitelisted !== 'boolean') {
        throw new Error('Whitelist check response missing whitelisted boolean');
      }

      return response;
    } catch (error) {
      console.error(`[CustomerApi] Whitelist check failed for ${email}:`, error);
      throw error;
    }
  }

  /**
 * Create a new waitlist profile
 */
async createWaitlistProfile(profileData: CreateWaitlistProfileRequest): Promise<WaitlistProfileResponse> {
  try {
    if (!profileData.email || !profileData.fullName || !profileData.project) { //phone no. not mandatory
      throw new Error('Email, full name, phone number, and project are required');
    }

    console.log(`[CustomerApi] Creating waitlist profile for: ${profileData.email}`);
    
    // Note: This is a public endpoint, so we don't include auth
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/waitlistProfiles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
    const errorText = await response.text();
    try {
      const errorJson = JSON.parse(errorText);
      
      // Create custom error that preserves the response structure
      const error = new Error(errorJson.error || errorJson.message || `HTTP ${response.status}: ${response.statusText}`);
      
      // Attach the full response data to the error for frontend access
      (error as any).response = {
        data: errorJson
      };
      
      throw error;
    } catch (parseError) {
      throw new Error(`API call failed [${response.status}]: ${errorText.substring(0, 200)}`);
    }
  }

    const data = await response.json();

    if (!data.success || !data.data) {
      throw new Error(data.message || 'Failed to create waitlist profile');
    }

    console.log(`[CustomerApi] Waitlist profile created successfully for: ${profileData.email}`);
    return data;
  } catch (error) {
    console.error(`[CustomerApi] Create waitlist profile failed for ${profileData.email}:`, error);
    throw error;
  }
}

/**
 * Check if a waitlist profile exists for an email
 */
async checkWaitlistProfile(email: string): Promise<CheckWaitlistProfileResponse> {
  try {
    if (!email) {
      throw new Error('Email is required');
    }

    console.log(`[CustomerApi] Checking waitlist profile for: ${email}`);
    
    // Note: This is a public endpoint, so we don't include auth
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/waitlistProfiles/check/${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.error || errorJson.message || `HTTP ${response.status}: ${response.statusText}`);
      } catch (parseError) {
        throw new Error(`API call failed [${response.status}]: ${errorText.substring(0, 200)}`);
      }
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to check waitlist profile');
    }

    console.log(`[CustomerApi] Waitlist profile check result for ${email}: ${data.exists ? 'exists' : 'not found'}`);
    return data;
  } catch (error) {
    console.error(`[CustomerApi] Check waitlist profile failed for ${email}:`, error);
    throw error;
  }
}

/**
 * Get waitlist profile by email (used internally when user logs in)
 */
async getWaitlistProfile(email: string): Promise<WaitlistProfileResponse> {
  try {
    if (!email) {
      throw new Error('Email is required');
    }

    console.log(`[CustomerApi] Getting waitlist profile for: ${email}`);
    
    // This is an authenticated endpoint since it's used internally
    const response = await apiFetch<any>(`/api/waitlistProfiles/${encodeURIComponent(email)}`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to get waitlist profile');
    }

    console.log(`[CustomerApi] Waitlist profile retrieved for: ${email}`);
    return response;
  } catch (error) {
    // Convert to info/debug with explicit fallback note
    console.info(
      `[CustomerApi] Waitlist profile unavailable for ${email}. This is normal if user didn't sign up via waitlist. Falling back to default handling (treat as direct signup).`
    );
    // Optionally log the raw error at debug level
    console.debug('[CustomerApi] Waitlist lookup error details:', error);
    throw error;
  }
}

// GeoAutomation API methods

// ===== GEOAUTOMATION MANAGEMENT =====

  /**
   * Get all geoAutomation scenes with enhanced filtering
   */
  async getAllGeoAutomationScenes(options: {
    category?: string;
    location?: string;
    search?: string;
  } = {}): Promise<GeoAutomationResponse> {
    try {
      const params = new URLSearchParams();
      if (options.category) params.append('category', options.category);
      if (options.location) params.append('location', options.location);
      if (options.search) params.append('search', options.search);

      const endpoint = `/api/geoAutomation{params.toString() ? '?' + params.toString() : ''}`;
      
      console.log(`[CustomerApi] Getting geoAutomation scenes with options:`, options);
      
      // Note: This is a public endpoint, so we don't include auth
      const response = await fetch(`${CUSTOMER_API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get geoAutomation scenes: ${errorText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get geoAutomation scenes');
      }

      console.log(`[CustomerApi] Retrieved ${data.count} geoAutomation scenes`);
      return data;
    } catch (error) {
      console.error(`[CustomerApi] Get geoAutomation scenes failed:`, error);
      throw error;
    }
  }

  /**
 * Get GeoAutomation scene by sceneId (NOT MongoDB _id)
 * This looks up by the sceneId field (e.g., "Terrebonne", "terrebonne")
 * 
 * @param sceneId - The sceneId to lookup (case-insensitive on backend)
 * @returns Single scene data
 */
async getGeoAutomationSceneBySceneId(sceneId: string): Promise<GeoAutomationSceneResponse> {
  try {
    if (!sceneId) {
      throw new Error('Scene ID is required');
    }

    console.log(`[CustomerApi] Getting GeoAutomation scene by sceneId: ${sceneId}`);

    // Note: This is a public endpoint, so we don't include auth
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/geoAutomation/scene/${encodeURIComponent(sceneId)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get GeoAutomation scene: ${errorText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to get GeoAutomation scene');
    }

    console.log(`[CustomerApi] Retrieved GeoAutomation scene: ${sceneId}`);
    return data;
  } catch (error) {
    console.error(`[CustomerApi] Get GeoAutomation scene by sceneId failed for ${sceneId}:`, error);
    throw error;
  }
}

  /**
   * Get geoAutomation scene by ID
   */
  async getGeoAutomationSceneById(id: string): Promise<GeoAutomationSceneResponse> {
    try {
      if (!id) {
        throw new Error('Scene ID is required');
      }

      console.log(`[CustomerApi] Getting geoAutomation scene: ${id}`);
      
      // Note: This is a public endpoint, so we don't include auth
      const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/geoAutomation/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get geoAutomation scene: ${errorText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get geoAutomation scene');
      }

      console.log(`[CustomerApi] Retrieved geoAutomation scene: ${id}`);
      return data;
    } catch (error) {
      console.error(`[CustomerApi] Get geoAutomation scene failed for ${id}:`, error);
      throw error;
    }
  }

 /**
 * Get POIs/Assets for a GeoAutomation scene
 * @param sceneId - The sceneId to get POIs for
 * @param params - Optional filter parameters
 * @returns POI data for the scene
 */
async getGeoAutomationPOIs(
  sceneId: string,
  params?: {
    type?: 'POINT' | 'LINESTRING' | 'POLYGON';
    page?: number;
    size?: number;
  }
): Promise<GeoAutomationPOIsResponse> {
  try {
    if (!sceneId) {
      throw new Error('Scene ID is required');
    }

    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const queryString = queryParams.toString();
    const endpoint = `/api/geoAutomation/scene/${encodeURIComponent(sceneId)}/pois${queryString ? '?' + queryString : ''}`;

    console.log(`[CustomerApi] Getting POIs for scene: ${sceneId}`);

    const response = await fetch(`${CUSTOMER_API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get POIs: ${errorText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to get POIs');
    }

    console.log(`[CustomerApi] Retrieved ${data.count} POIs for scene ${sceneId}`);
    return data;
  } catch (error) {
    console.error(`[CustomerApi] Get POIs failed for ${sceneId}:`, error);
    throw error;
  }
} 

  /**
   * Get available geoAutomation categories
   */
  async getGeoAutomationCategories(): Promise<GeoAutomationCategoriesResponse> {
    try {
      console.log(`[CustomerApi] Getting geoAutomation categories`);

      // Note: This is a public endpoint, so we don't include auth
      const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/geoAutomation/categories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get geoAutomation categories: ${errorText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get geoAutomation categories');
      }

      console.log(`[CustomerApi] Retrieved ${data.count} geoAutomation categories`);
      return data;
    } catch (error) {
      console.error(`[CustomerApi] Get geoAutomation categories failed:`, error);
      throw error;
    }
  }

  /**
   * Search geoAutomation scenes
   */
  async searchGeoAutomationScenes(searchTerm: string): Promise<GeoAutomationSearchResponse> {
    try {
      if (!searchTerm) {
        throw new Error('Search term is required');
      }

      console.log(`[CustomerApi] Searching geoAutomation scenes for: ${searchTerm}`);

      // Note: This is a public endpoint, so we don't include auth
      const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/geoAutomation/search?q=${encodeURIComponent(searchTerm)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to search geoAutomation scenes: ${errorText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to search geoAutomation scenes');
      }

      console.log(`[CustomerApi] Found ${data.count} geoAutomation scenes for search: ${searchTerm}`);
      return data;
    } catch (error) {
      console.error(`[CustomerApi] Search geoAutomation scenes failed for ${searchTerm}:`, error);
      throw error;
    }
  }

  /**
   * Get geoAutomation scenes by category
   */
  async getGeoAutomationScenesByCategory(category: string): Promise<GeoAutomationResponse> {
    try {
      if (!category) {
        throw new Error('Category is required');
      }

      console.log(`[CustomerApi] Getting geoAutomation scenes for category: ${category}`);

      // Note: This is a public endpoint, so we don't include auth
      const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/geoAutomation/category/${encodeURIComponent(category)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get geoAutomation scenes by category: ${errorText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get geoAutomation scenes by category');
      }

      console.log(`[CustomerApi] Retrieved ${data.count} geoAutomation scenes for category: ${category}`);
      return data;
    } catch (error) {
      console.error(`[CustomerApi] Get geoAutomation scenes by category failed for ${category}:`, error);
      throw error;
    }
  }

  //imageAssets for  scenes
  /**
   * Get pre-signed GET URLs for a batch of ydrive image S3 keys.
   * Keys are plain filenames e.g. "00004021_00000000_c0.JPG"
   */
  async getImagePresignedUrls(
    imageNames: string[],
    expiresIn = 86400,
  ): Promise<ImagePresignedUrlsResponse> {
    try {
      console.log(`[CustomerApi] Getting presigned URLs for ${imageNames.length} images`);
 
      const response = await apiFetch<ImagePresignedUrlsResponse>(
        '/api/image-assets/presigned-urls',
        {
          method: 'POST',
          body: JSON.stringify({ imageNames, expiresIn }),
        },
      );
 
      if (!response.success || !response.data) {
        throw new Error('Failed to get image presigned URLs');
      }
 
      console.log(`[CustomerApi] Got ${response.data.count} image URLs`);
      return response;
    } catch (error) {
      console.error('[CustomerApi] getImagePresignedUrls failed:', error);
      throw error;
    }
  }

  async getSceneAssetLabels(sceneId: string): Promise<AssetLibraryLabelsResponse> {
    return apiFetch<AssetLibraryLabelsResponse>(
      `/api/asset-library/scenes/${encodeURIComponent(sceneId)}/labels`,
    );
  }

  async createSceneAssetLabel(
    sceneId: string,
    data: { name: string; world: [number, number, number]; properties?: Record<string, unknown> },
  ): Promise<AssetLibraryLabelResponse> {
    return apiFetch<AssetLibraryLabelResponse>(
      `/api/asset-library/scenes/${encodeURIComponent(sceneId)}/labels`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  async upsertViewerAgentSceneAssetLabel(
    sceneId: string,
    data: { name: string; world: [number, number, number]; properties?: Record<string, unknown> },
  ): Promise<AssetLibraryLabelResponse> {
    return apiFetch<AssetLibraryLabelResponse>(
      `/api/asset-library/scenes/${encodeURIComponent(sceneId)}/labels/upsert-viewer-agent`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  async updateSceneAssetLabel(
    sceneId: string,
    assetId: string,
    data: { name?: string; world?: [number, number, number]; properties?: Record<string, unknown> },
  ): Promise<AssetLibraryLabelResponse> {
    return apiFetch<AssetLibraryLabelResponse>(
      `/api/asset-library/scenes/${encodeURIComponent(sceneId)}/labels/${encodeURIComponent(assetId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  }

  async deleteSceneAssetLabel(sceneId: string, assetId: string): Promise<{ success: boolean }> {
    return apiFetch<{ success: boolean }>(
      `/api/asset-library/scenes/${encodeURIComponent(sceneId)}/labels/${encodeURIComponent(assetId)}`,
      { method: 'DELETE' },
    );
  }
 

// Collectiv3 API methods

  async getProgressionScene(id: string): Promise<ProgressionSceneResponse> {
    if (!id) throw new Error('Progression scene ID is required');
    const response = await fetch(
      `${CUSTOMER_API_BASE_URL}/api/progressions/${encodeURIComponent(id)}`,
      { headers: { 'Content-Type': 'application/json' } },
    );
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to get progression scene');
    }
    return data;
  }

  async getAllProgressions(): Promise<{ success: boolean; data: ProgressionScene[] }> {
    const response = await fetch(
      `${CUSTOMER_API_BASE_URL}/api/progressions`,
      { headers: { 'Content-Type': 'application/json' } },
    );
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to get progressions');
    return data;
  }

// ===== COLLECTIV3 MANAGEMENT =====

  /**
   * Get all collectiv3 scenes with enhanced filtering
   */
  async getAllCollectiv3Scenes(options: {
    category?: string;
    location?: string;
    search?: string;
  } = {}): Promise<Collectiv3Response> {
    try {
      const params = new URLSearchParams();
      if (options.category) params.append('category', options.category);
      if (options.location) params.append('location', options.location);
      if (options.search) params.append('search', options.search);

      const endpoint = `/api/collectiv3${params.toString() ? '?' + params.toString() : ''}`;
      
      console.log(`[CustomerApi] Getting collectiv3 scenes with options:`, options);
      
      // Note: This is a public endpoint, so we don't include auth
      const response = await fetch(`${CUSTOMER_API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get collectiv3 scenes: ${errorText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get collectiv3 scenes');
      }

      console.log(`[CustomerApi] Retrieved ${data.count} collectiv3 scenes`);
      return data;
    } catch (error) {
      console.error(`[CustomerApi] Get collectiv3 scenes failed:`, error);
      throw error;
    }
  }

  /**
   * Get collectiv3 scene by ID
   */
  async getCollectiv3SceneById(id: string): Promise<Collectiv3SceneResponse> {
    try {
      if (!id) {
        throw new Error('Scene ID is required');
      }

      console.log(`[CustomerApi] Getting collectiv3 scene: ${id}`);
      
      // Note: This is a public endpoint, so we don't include auth
      const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/collectiv3/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get collectiv3 scene: ${errorText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get collectiv3 scene');
      }

      console.log(`[CustomerApi] Retrieved collectiv3 scene: ${id}`);
      return data;
    } catch (error) {
      console.error(`[CustomerApi] Get collectiv3 scene failed for ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get available collectiv3 categories
   */
  async getCollectiv3Categories(): Promise<Collectiv3CategoriesResponse> {
    try {
      console.log(`[CustomerApi] Getting collectiv3 categories`);
      
      // Note: This is a public endpoint, so we don't include auth
      const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/collectiv3/categories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get collectiv3 categories: ${errorText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get collectiv3 categories');
      }

      console.log(`[CustomerApi] Retrieved ${data.count} collectiv3 categories`);
      return data;
    } catch (error) {
      console.error(`[CustomerApi] Get collectiv3 categories failed:`, error);
      throw error;
    }
  }

  /**
   * Search collectiv3 scenes
   */
  async searchCollectiv3Scenes(searchTerm: string): Promise<Collectiv3SearchResponse> {
    try {
      if (!searchTerm) {
        throw new Error('Search term is required');
      }

      console.log(`[CustomerApi] Searching collectiv3 scenes for: ${searchTerm}`);
      
      // Note: This is a public endpoint, so we don't include auth
      const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/collectiv3/search?q=${encodeURIComponent(searchTerm)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to search collectiv3 scenes: ${errorText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to search collectiv3 scenes');
      }

      console.log(`[CustomerApi] Found ${data.count} collectiv3 scenes for search: ${searchTerm}`);
      return data;
    } catch (error) {
      console.error(`[CustomerApi] Search collectiv3 scenes failed for ${searchTerm}:`, error);
      throw error;
    }
  }

  /**
   * Get collectiv3 scenes by category
   */
  async getCollectiv3ScenesByCategory(category: string): Promise<Collectiv3Response> {
    try {
      if (!category) {
        throw new Error('Category is required');
      }

      console.log(`[CustomerApi] Getting collectiv3 scenes for category: ${category}`);
      
      // Note: This is a public endpoint, so we don't include auth
      const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/collectiv3/category/${encodeURIComponent(category)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get collectiv3 scenes by category: ${errorText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get collectiv3 scenes by category');
      }

      console.log(`[CustomerApi] Retrieved ${data.count} collectiv3 scenes for category: ${category}`);
      return data;
    } catch (error) {
      console.error(`[CustomerApi] Get collectiv3 scenes by category failed for ${category}:`, error);
      throw error;
    }
  }

  // ===== PROFILES (ADMIN + CURRENT USER) =====

  async getAllProfiles(): Promise<{ success: boolean; data: ProfileData[] }> {
    const response = await apiFetch<{ success: boolean; data: ProfileData[] }>(`/api/profiles/all`);
    if (!response.success || !Array.isArray(response.data)) {
      throw new Error('Failed to fetch profiles');
    }
    return response;
  }

  async getCurrentProfile(): Promise<ProfileData> {
    // Reuse existing profiles endpoint which returns current user's profile
    const response = await apiFetch<ProfileData>(`/api/profiles`);
    return response;
  }

  async completeUpload(uploadData: any): Promise<any> {
    try {
      if (!uploadData.uploadId) {
        throw new Error('Upload ID is required to complete upload');
      }

      console.log(`[CustomerApi] Completing upload: ${uploadData.uploadId}`);
      
      const response = await apiFetch<any>('/api/uploads/complete', {
        method: 'POST',
        body: JSON.stringify(uploadData)
      });

      console.log(`[CustomerApi] Upload completed successfully: ${uploadData.uploadId}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Complete upload failed for ${uploadData.uploadId}:`, error);
      throw error;
    }
  }

  async getUploadStatus(uploadId: string): Promise<any> {
    try {
      if (!uploadId) {
        throw new Error('Upload ID is required to get upload status');
      }

      const response = await apiFetch<any>(`/api/uploads/${uploadId}/status`);
      
      // Validate response structure
      validateUploadStatusResponse(response);

      return response;
    } catch (error) {
      console.error(`[CustomerApi] Get upload status failed for ${uploadId}:`, error);
      throw error;
    }
  }

  // S3 Rename Operations (from old version)
  async renameDraftToFinal(request: { sessionUUID: string; draftFolderPath: string; finalFolderPath: string }): Promise<any> {
    try {
      if (!request.sessionUUID || !request.draftFolderPath || !request.finalFolderPath) {
        throw new Error('Session UUID, draft folder path, and final folder path are required');
      }

      console.log(`[CustomerApi] Renaming draft to final: ${request.draftFolderPath} -> ${request.finalFolderPath}`);
      
      const response = await apiFetch<any>('/api/uploads/rename-draft-to-final', {
        method: 'POST',
        body: JSON.stringify(request)
      });

      console.log(`[CustomerApi] Rename operation initiated for session: ${request.sessionUUID}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Rename draft to final failed:`, error);
      throw error;
    }
  }

  async getRenameOperationStatus(operationId: string): Promise<any> {
    try {
      if (!operationId) {
        throw new Error('Operation ID is required to get rename status');
      }

      const response = await apiFetch<any>(`/api/uploads/rename-status/${operationId}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Get rename operation status failed for ${operationId}:`, error);
      throw error;
    }
  }

  async markReadyForReconstruction(data: { sessionUUID: string; finalFolderPath: string }): Promise<any> {
    try {
      if (!data.sessionUUID || !data.finalFolderPath) {
        throw new Error('Session UUID and final folder path are required');
      }

      console.log(`[CustomerApi] Marking ready for reconstruction: ${data.sessionUUID}`);
      
      const response = await apiFetch<any>('/api/uploads/mark-ready-for-reconstruction', {
        method: 'POST',
        body: JSON.stringify(data)
      });

      console.log(`[CustomerApi] Marked ready for reconstruction: ${data.sessionUUID}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Mark ready for reconstruction failed:`, error);
      throw error;
    }
  }

  // Scan Management with Enhanced Lifecycle Support
  async createScan(scanData: CreateScanDto): Promise<ScanCreationResponse> {
    try {
      if (!scanData) {
        throw new Error('Scan data is required');
      }

      // Ensure isFromMobileApp is set to false for web app
      const webScanData = {
        ...scanData,
        isFromMobileApp: false,
        status: scanData.status || 'tempsaved' as ScanStatus
      };

      console.log(`[CustomerApi] Creating scan:`, webScanData);
      
      const response = await apiFetch<ApiResponse<{ scanId: string; scan: Scan }>>('/api/scans', {
        method: 'POST',
        body: JSON.stringify(webScanData)
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create scan');
      }

      console.log(`[CustomerApi] Scan created successfully: ${response.data.scanId}`);
      return {
        scanId: response.data.scanId,
        scan: response.data.scan
      };
    } catch (error) {
      console.error(`[CustomerApi] Create scan failed:`, error);
      throw error;
    }
  }

  async createScanWithStatus(scanData: CreateScanDto, status: ScanStatus): Promise<ScanCreationResponse> {
    return this.createScan({
      ...scanData,
      status,
      isFromMobileApp: false
    });
  }

  async getScans(filters?: ScanFilterDto): Promise<Scan[]> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value.toString());
          }
        });
      }

      const url = `/api/scans${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiFetch<ApiResponse<Scan[]>>(url);
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to retrieve scans');
      }

      if (!Array.isArray(response.data)) {
        throw new Error('Expected array response for scans');
      }

      console.log(`[CustomerApi] Retrieved ${response.data.length} scans`);
      console.log('🟢 [CRITICAL] Scan data:', response.data.map(scan => ({
        scanId: scan.scanId,
        status: scan.status,
        hasInputImageCount: scan.inputImageCount !== undefined,
        inputImageCount: scan.inputImageCount,
        hasSequenceUuid: scan.sequenceUuid !== undefined
      })));
      return response.data;
    } catch (error) {
      console.error(`[CustomerApi] Get scans failed:`, error);
      throw error;
    }
  }

  async getScanById(scanId: string): Promise<Scan> {
    try {
      if (!scanId) {
        throw new Error('Scan ID is required');
      }

      const response = await apiFetch<ApiResponse<Scan>>(`/api/scans/${scanId}`);
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to retrieve scan');
      }

      return response.data;
    } catch (error) {
      console.error(`[CustomerApi] Get scan by ID failed for ${scanId}:`, error);
      throw error;
    }
  }

  async updateScan(scanId: string, scanData: UpdateScanDto): Promise<Scan> {
    try {
      if (!scanId || !scanData) {
        throw new Error('Scan ID and scan data are required');
      }

      console.log(`[CustomerApi] Updating scan ${scanId} with enhanced version sync`);
      
      // Step 1: Update the scan document
      const response = await apiFetch<ApiResponse<Scan>>(`/api/scans/${scanId}`, {
        method: 'PATCH',
        body: JSON.stringify(scanData)
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update scan');
      }

      const updatedScan = response.data;
      console.log(`[CustomerApi] Scan updated successfully: ${scanId}`);

      // Step 2: Patch the active scan version if any of its mirrored fields changed
      const fieldsRequiringScanVersionUpdate = [
        'status', 'subStatus', 'reconstructionFolder', 'thumbnailUrl',
        'flythroughUrl', 'tempFolderPath', 's3FolderPath',
        'sequenceUuid', 'gaussModelPath', 'pipelineJsonPath', 'inputImageCount',
        'fileSize', 'quality'
      ];

      const needsVersionUpdate = fieldsRequiringScanVersionUpdate.some(
        field => (scanData as any)[field] !== undefined
      );

      if (needsVersionUpdate && updatedScan.activeVersionId) {
        try {
          console.log(`[CustomerApi] Patching active scan version: ${updatedScan.activeVersionId}`);

          // Fetch current version to keep untouched fields intact
          const currentVersion = await this.getScanVersion(updatedScan.activeVersionId);

          const versionUpdateData: UpdateScanVersionDto = {
            status: (scanData.status ?? currentVersion.status) as any,
            subStatus: (scanData.subStatus ?? currentVersion.subStatus) as any,
            reconstructionFolder: (scanData.reconstructionFolder ?? currentVersion.reconstructionFolder) as any,
            thumbnailUrl: (scanData.thumbnailUrl ?? currentVersion.thumbnailUrl) as any,
            flythroughUrl: (scanData.flythroughUrl ?? currentVersion.flythroughUrl) as any,
            tempFolderPath: (scanData.tempFolderPath ?? currentVersion.tempFolderPath) as any,
            s3FolderPath: (scanData.s3FolderPath ?? currentVersion.s3FolderPath) as any,
            sequenceUuid: (scanData.sequenceUuid ?? currentVersion.sequenceUuid) as any,
            gaussModelPath: (scanData.gaussModelPath ?? currentVersion.gaussModelPath) as any,
            pipelineJsonPath: (scanData.pipelineJsonPath ?? currentVersion.pipelineJsonPath) as any,
            inputImageCount: (scanData.inputImageCount ?? currentVersion.inputImageCount) as any,
            fileSize: (scanData.fileSize ?? currentVersion.fileSize) as any,
            quality: (scanData.quality ?? currentVersion.quality) as any,
            changelog: `Synced with scan update: ${Object.keys(scanData).join(', ')}`
          };

          await this.updateScanVersion(updatedScan.activeVersionId, versionUpdateData);

        } catch (versionError) {
          console.warn('[CustomerApi] Failed to patch scan version:', versionError);
          // Swallow error, main scan update has succeeded
        }
      }

      return updatedScan;
    } catch (error) {
      console.error(`[CustomerApi] Update scan failed for ${scanId}:`, error);
      throw error;
    }
  }

  async updateScanStatus(scanId: string, status: ScanStatus, subStatus?: string): Promise<Scan> {
    const updateData: UpdateScanDto = { status };
    if (subStatus) {
      updateData.subStatus = subStatus;
    }
    
    return this.updateScan(scanId, updateData);
  }


/**
 * Get thumbnail URL for a scan via gallery service
 */
  async getThumbnailForScan(scanId: string): Promise<string | null> {
    try {
      if (!scanId) {
        throw new Error('Scan ID is required');
      }

      console.log(`[CustomerApi] Getting thumbnail for scan: ${scanId}`);
      
      const response = await apiFetch<{ success: boolean; thumbnailUrl: string | null }>(
        `/api/gallery/thumbnail/${scanId}`
      );

      if (response.success && response.thumbnailUrl) {
        console.log(`[CustomerApi] Thumbnail URL retrieved for scan: ${scanId}`);
        return response.thumbnailUrl;
      } else {
        console.log(`[CustomerApi] No thumbnail available for scan: ${scanId}`);
        return null;
      }
    } catch (error) {
      console.error(`[CustomerApi] Failed to get thumbnail for scan ${scanId}:`, error);
      // Don't throw - just return null so UI can handle gracefully
      return null;
    }
  }

  async deleteScan(scanId: string): Promise<boolean> {
    try {
      if (!scanId) {
        throw new Error('Scan ID is required');
      }

      console.log(`[CustomerApi] Deleting scan ${scanId}`);
      
      const response = await apiFetch<ApiResponse<void>>(`/api/scans/${scanId}`, {
        method: 'DELETE'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete scan');
      }

      console.log(`[CustomerApi] Scan deleted successfully: ${scanId}`);
      return true;
    } catch (error) {
      console.error(`[CustomerApi] Delete scan failed for ${scanId}:`, error);
      throw error;
    }
  }

  // Scan Metadata Management
  async createScanMetadata(metadataData: CreateScanMetadataDto): Promise<ScanMetadata> {
    try {
      if (!metadataData || !metadataData.scanId || !metadataData.metadata) {
        throw new Error('Scan ID and metadata are required');
      }

      console.log(`[CustomerApi] Creating scan metadata for scan: ${metadataData.scanId}`);
      
      const response = await apiFetch<ApiResponse<ScanMetadata>>('/api/scans/metadata', {
        method: 'POST',
        body: JSON.stringify(metadataData)
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create scan metadata');
      }

      console.log(`[CustomerApi] Scan metadata created successfully for scan: ${metadataData.scanId}`);
      return response.data;
    } catch (error) {
      console.error(`[CustomerApi] Create scan metadata failed:`, error);
      throw error;
    }
  }

  async getScanMetadata(scanId: string): Promise<ScanMetadata> {
    try {
      if (!scanId) {
        throw new Error('Scan ID is required');
      }

      const response = await apiFetch<ApiResponse<ScanMetadata>>(`/api/scans/metadata/${scanId}`);
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to retrieve scan metadata');
      }

      return response.data;
    } catch (error) {
      console.error(`[CustomerApi] Get scan metadata failed for ${scanId}:`, error);
      throw error;
    }
  }

  async updateScanMetadata(metadataId: string, metadataData: UpdateScanMetadataDto): Promise<ScanMetadata> {
    try {
      if (!metadataId || !metadataData || !metadataData.metadata) {
        throw new Error('Metadata ID and metadata payload are required');
      }

      console.log(`[CustomerApi] Updating scan metadata: ${metadataId}`);
      
      const response = await apiFetch<ApiResponse<ScanMetadata>>(`/api/scans/metadata/${metadataId}`, {
        method: 'PATCH',
        body: JSON.stringify(metadataData)
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update scan metadata');
      }

      console.log(`[CustomerApi] Scan metadata updated successfully: ${metadataId}`);
      return response.data;
    } catch (error) {
      console.error(`[CustomerApi] Update scan metadata failed for ${metadataId}:`, error);
      throw error;
    }
  }

  async deleteScanMetadata(metadataId: string): Promise<boolean> {
    try {
      if (!metadataId) {
        throw new Error('Metadata ID is required');
      }

      console.log(`[CustomerApi] Deleting scan metadata: ${metadataId}`);
      
      const response = await apiFetch<ApiResponse<void>>(`/api/scans/metadata/${metadataId}`, {
        method: 'DELETE'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete scan metadata');
      }

      console.log(`[CustomerApi] Scan metadata deleted successfully: ${metadataId}`);
      return true;
    } catch (error) {
      console.error(`[CustomerApi] Delete scan metadata failed for ${metadataId}:`, error);
      throw error;
    }
  }

  /**
 * Get Gaussian splat object for a scan
 */
  async getGaussianSplatObject(scanId: string): Promise<{
  success: boolean;
  data: {
    scanId: string;
    gaussianFile: {
      fileName: string;
      fileKey: string;
      size: number;
      lastModified: Date;
      signedUrl: string;
      fileType: string;
    };
    helperFiles?: Array<{
      fileName: string;
      fileKey: string;
      fileType: 'env-map' | 'poses-json' | 'other';
      size: number;
      lastModified: string | Date;
      signedUrl: string;
    }>;
  };
}> {
    try {
      if (!scanId) {
        throw new Error('Scan ID is required');
      }

      console.log(`[CustomerApi] Getting Gaussian splat object for scan: ${scanId}`);
      
      const response = await apiFetch<{
        success: boolean;
        data: {
          scanId: string;
          gaussianFile: {
            fileName: string;
            fileKey: string;
            size: number;
            lastModified: Date;
            signedUrl: string;
            fileType: string;
          };
        };
      }>(`/api/gallery/gaussian-splat/${scanId}`);

      if (!response.success || !response.data) {
        throw new Error('Failed to get Gaussian splat object');
      }

      console.log(`[CustomerApi] Gaussian splat object retrieved successfully for scan: ${scanId}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Get Gaussian splat object failed for ${scanId}:`, error);
      throw error;
    }
  }

  // Scan Lifecycle Management Methods
  async transitionToUploaded(scanId: string, s3FolderPath: string, inputImageCount: number, sequenceUuid: string): Promise<Scan> {
    return this.updateScan(scanId, {
      status: ScanStatus.Uploaded,
      s3FolderPath,
      inputImageCount,
      sequenceUuid
    });
  }

  async transitionToProcessing(scanId: string): Promise<Scan> {
    return this.updateScan(scanId, {
      status: ScanStatus.Processing,
      subStatus: 'Processing started'
    });
  }

  async transitionToCompleted(scanId: string, reconstructionFolder?: string): Promise<Scan> {
    const updateData: UpdateScanDto = { status: ScanStatus.Completed };
    if (reconstructionFolder) {
      updateData.reconstructionFolder = reconstructionFolder;
    }
    return this.updateScan(scanId, updateData);
  }

  async transitionToFailed(scanId: string, errorMessage?: string): Promise<Scan> {
    return this.updateScan(scanId, {
      status: ScanStatus.Failed,
      subStatus: errorMessage || 'Processing failed'
    });
  }

  // Reconstruction with Enhanced Lifecycle Integration
  async triggerReconstruction(reconstructionData: TriggerReconstructionDto): Promise<ApiResponse<any>> {
    try {
      if (!reconstructionData) {
        throw new Error('Reconstruction data is required');
      }

      // Backfill preset if not provided (prefer capturePreset from metadata, then fallback to scan.category)
      if (!reconstructionData.preset && reconstructionData.scanId) {
        try {
          // Try to read capturePreset from scan metadata
          try {
            const existingMetadata = await this.getScanMetadata(reconstructionData.scanId);
            const capturePreset = (existingMetadata && (existingMetadata as any).metadata && (existingMetadata as any).metadata.capturePreset) || undefined;
            if (capturePreset) {
              reconstructionData = { ...reconstructionData, preset: capturePreset };
            }
          } catch {
            // ignore metadata read failure
          }
        } catch (_e) {
          // Non-fatal: continue without preset if lookup fails
        }
      }

      console.log(`[CustomerApi] Triggering reconstruction:`, reconstructionData);
      
      // 1) Attempt to trigger reconstruction first
      let response: ApiResponse<any>;
      try {
        response = await apiFetch<ApiResponse<any>>('/api/reconstructions/trigger', {
          method: 'POST',
          body: JSON.stringify(reconstructionData)
        });
      } catch (httpError: any) {
        // Network / HTTP failure ‑ mark scan as failed (if scanId present) and propagate
        if (reconstructionData.scanId) {
          try {
            await this.transitionToFailed(
              reconstructionData.scanId,
              httpError instanceof Error ? httpError.message : 'Failed to trigger reconstruction'
            );
          } catch (statusError) {
            console.warn('[CustomerApi] Failed to update scan status to failed:', statusError);
          }
        }
        throw httpError;
      }

      // 2) Backend responded but with success: false
      if (!response.success) {
        if (reconstructionData.scanId) {
          try {
            await this.transitionToFailed(
              reconstructionData.scanId,
              response.error || 'Failed to trigger reconstruction'
            );
          } catch (statusError) {
            console.warn('[CustomerApi] Failed to update scan status to failed:', statusError);
          }
        }
        throw new Error(response.error || 'Failed to trigger reconstruction');
      }

      // Detect immediate failure even when success === true (e.g., Hive returns execution_status: "failed")
      const immediateFail = (() => {
        const msg = ((response.error ?? response.message) || '').toLowerCase();
        if (msg.includes('request handling failed')) return true;
        if (response.data && typeof response.data === 'object') {
          const data: any = response.data;
          if (data.execution_status === 'failed') return true;
          if (data.success === false) return true;
        }
        return false;
      })();

      if (immediateFail) {
        const failReason = response.error || response.message || 'Reconstruction trigger failed';
        if (reconstructionData.scanId) {
          try {
            await this.transitionToFailed(reconstructionData.scanId, failReason);
          } catch (statusError) {
            console.warn('[CustomerApi] Failed to update scan status to failed:', statusError);
          }
        }
        throw new Error(failReason);
      }

      // 3) Successful trigger – now mark scan as processing
      if (reconstructionData.scanId) {
        try {
          await this.updateScanStatus(
            reconstructionData.scanId,
            ScanStatus.Processing,
            'Reconstruction trigger accepted'
          );
          console.log(`[CustomerApi] Scan ${reconstructionData.scanId} status updated to processing`);
        } catch (statusError) {
          console.warn(`[CustomerApi] Failed to update scan status to processing:`, statusError);
        }
      }

      console.log(`[CustomerApi] Reconstruction triggered successfully`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Trigger reconstruction failed:`, error);
      throw error;
    }
  }

  /**
   * Check reconstruction status for a given sequence UUID
   */
  async getReconstructionStatus(sequenceUuid: string): Promise<ReconstructionStatusResponse> {
    try {
      if (!sequenceUuid) {
        throw new Error('Sequence UUID is required');
      }

      console.log(`[CustomerApi] Checking reconstruction status for: ${sequenceUuid}`);
      
      const response = await apiFetch<ReconstructionStatusResponse>(`/api/reconstructions/${sequenceUuid}/status`);

      if (!response.success) {
        throw new Error('Failed to get reconstruction status');
      }

      console.log(`[CustomerApi] Reconstruction status retrieved:`, response.data);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Get reconstruction status failed for ${sequenceUuid}:`, error);
      throw error;
    }
  }

  // Scan Version Management
  async createScanVersion(scanId: string, versionData: CreateScanVersionDto): Promise<ScanVersion> {
    try {
      if (!scanId || !versionData) {
        throw new Error('Scan ID and version data are required');
      }

      console.log(`[CustomerApi] Creating new version for scan: ${scanId}`);
      
      const response = await apiFetch<ApiResponse<ScanVersion>>('/api/scans/versions', {
        method: 'POST',
        body: JSON.stringify({
          ...versionData,
          scanId,
          isFromMobileApp: false // Always false for web app
        })
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create scan version');
      }

      console.log(`[CustomerApi] Scan version created successfully: ${response.data.versionId}`);
      return response.data;
    } catch (error) {
      console.error(`[CustomerApi] Create scan version failed:`, error);
      throw error;
    }
  }

  async getScanVersions(scanId: string): Promise<ScanVersion[]> {
    try {
      if (!scanId) {
        throw new Error('Scan ID is required');
      }

      const response = await apiFetch<ApiResponse<ScanVersion[]>>(`/api/scans/${scanId}/versions`);
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to retrieve scan versions');
      }

      if (!Array.isArray(response.data)) {
        throw new Error('Expected array response for scan versions');
      }

      console.log(`[CustomerApi] Retrieved ${response.data.length} versions for scan: ${scanId}`);
      return response.data;
    } catch (error) {
      console.error(`[CustomerApi] Get scan versions failed for ${scanId}:`, error);
      throw error;
    }
  }

  async getScanVersion(versionId: string): Promise<ScanVersion> {
    try {
      if (!versionId) {
        throw new Error('Version ID is required');
      }

      const response = await apiFetch<ApiResponse<ScanVersion>>(`/api/scans/versions/${versionId}`);
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to retrieve scan version');
      }

      return response.data;
    } catch (error) {
      console.error(`[CustomerApi] Get scan version failed for ${versionId}:`, error);
      throw error;
    }
  }

  async setActiveScanVersion(scanId: string, versionId: string): Promise<Scan> {
    try {
      if (!scanId || !versionId) {
        throw new Error('Scan ID and version ID are required');
      }

      console.log(`[CustomerApi] Setting active version for scan ${scanId} to version: ${versionId}`);
      
      const response = await apiFetch<ApiResponse<Scan>>(`/api/scans/${scanId}/active-version`, {
        method: 'PATCH',
        body: JSON.stringify({ versionId })
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to set active scan version');
      }

      console.log(`[CustomerApi] Active version set successfully for scan: ${scanId}`);
      return response.data;
    } catch (error) {
      console.error(`[CustomerApi] Set active scan version failed:`, error);
      throw error;
    }
  }

  /**
   * Patch an existing scan version (keeps active version in sync with Scan).
   */
  async updateScanVersion(versionId: string, versionData: UpdateScanVersionDto): Promise<ScanVersion> {
    if (!versionId) {
      throw new Error('Version ID is required');
    }

    const response = await apiFetch<ApiResponse<ScanVersion>>(`/api/scans/versions/${versionId}`, {
      method: 'PATCH',
      body: JSON.stringify(versionData)
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to update scan version');
    }

    return response.data;
  }

  // ===== GAUS MODEL MANAGEMENT =====

  /**
   * Get temporary credentials for S3 gaus model reads
   */
  async getGausCredentials(): Promise<GausCredentialsResponse> {
  try {
    console.log(`[CustomerApi] 🖼️ Getting gaus library credentials via gallery session pooling...`);
    
    // Use the session manager for better session management
    const galleryResponse = await s3CredentialsManager.getGalleryCredentials();
    
    // Convert to the expected gaus credentials format
    const gausResponse: GausCredentialsResponse = {
      success: true,
      data: {
        credentials: galleryResponse.data.credentials,
        region: galleryResponse.data.region,
        bucket: galleryResponse.data.bucket
      }
    };

    console.log(`[CustomerApi] ✅ Gaus library credentials obtained via gallery session pooling`);
    return gausResponse;
  } catch (error) {
    console.error(`[CustomerApi] Get gaus credentials failed:`, error);
    throw error;
  }
}

  /**
   * List all gaus models from library
   */
  async listGausModels(options: {
    prefix?: string;
    maxKeys?: number;
    continuationToken?: string;
  } = {}): Promise<GausModelsResponse> {
    try {
      const params = new URLSearchParams();
      
      if (options.prefix) params.append('prefix', options.prefix);
      if (options.maxKeys) params.append('maxKeys', options.maxKeys.toString());
      if (options.continuationToken) params.append('continuationToken', options.continuationToken);

      const endpoint = `/api/gallery/models${params.toString() ? '?' + params.toString() : ''}`;
      
      console.log(`[CustomerApi] Listing gaus models with params:`, options);
      
      const response = await apiFetch<GausModelsResponse>(endpoint);

      if (!response.success || !response.data) {
        throw new Error('Failed to list gaus models');
      }

      console.log(`[CustomerApi] Retrieved ${response.data.objects.length} gaus models`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] List gaus models failed:`, error);
      throw error;
    }
  }

  /**
   * List user-specific gaus models
   */
  async listUserGausModels(options: {
    maxKeys?: number;
    continuationToken?: string;
  } = {}): Promise<GausModelsResponse> {
    try {
      const params = new URLSearchParams();
      
      if (options.maxKeys) params.append('maxKeys', options.maxKeys.toString());
      if (options.continuationToken) params.append('continuationToken', options.continuationToken);

      const endpoint = `/api/gallery/user-models${params.toString() ? '?' + params.toString() : ''}`;
      
      console.log(`[CustomerApi] Listing user gaus models with params:`, options);
      
      const response = await apiFetch<GausModelsResponse>(endpoint);

      if (!response.success || !response.data) {
        throw new Error('Failed to list user gaus models');
      }

      console.log(`[CustomerApi] Retrieved ${response.data.objects.length} user gaus models`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] List user gaus models failed:`, error);
      throw error;
    }
  }

  /**
   * Get signed URL for a specific gaus model
   */
  async getGausModelUrl(objectKey: string, expiresIn: number = 3600): Promise<GausModelUrlResponse> {
    try {
      if (!objectKey) {
        throw new Error('Object key is required');
      }

      console.log(`[CustomerApi] Getting gaus model URL for: ${objectKey}`);
      
      const response = await apiFetch<GausModelUrlResponse>('/api/gallery/model-url', {
        method: 'POST',
        body: JSON.stringify({
          objectKey,
          expiresIn
        })
      });

      if (!response.success || !response.data) {
        throw new Error('Failed to get gaus model URL');
      }

      console.log(`[CustomerApi] Gaus model URL generated successfully for: ${objectKey}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Get gaus model URL failed for ${objectKey}:`, error);
      throw error;
    }
  }

  /**
   * Get gaus library status/health check
   */
  async getGausLibraryStatus(): Promise<GausLibraryStatusResponse> {
    try {
      console.log(`[CustomerApi] Getting gaus library status`);
      
      const response = await apiFetch<GausLibraryStatusResponse>('/api/gallery/status');

      if (!response.success || !response.data) {
        throw new Error('Failed to get gaus library status');
      }

      console.log(`[CustomerApi] Gaus library status retrieved successfully`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Get gaus library status failed:`, error);
      throw error;
    }
  }

  // ===== SHARING MANAGEMENT =====

  /**
   * Share a scan via email
   */
  async shareScan(scanId: string, shareData: ShareScanRequest): Promise<ShareScanResponse> {
    try {
      if (!scanId || !shareData.email) {
        throw new Error('Scan ID and email are required for sharing');
      }

      console.log(`[CustomerApi] Sharing scan ${scanId} with ${shareData.email}`);
      
      const response = await apiFetch<ShareScanResponse>(`/api/scans/${scanId}/share`, {
        method: 'POST',
        body: JSON.stringify(shareData)
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to share scan');
      }

      console.log(`[CustomerApi] Scan shared successfully: ${scanId} with ${shareData.email}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Share scan failed for ${scanId}:`, error);
      throw error;
    }
  }

  /**
   * Get shares sent by current user
   */
  async getSharesSent(options: {
    status?: 'pending' | 'accepted' | 'revoked';
    limit?: number;
    offset?: number;
  } = {}): Promise<GetSharesResponse> {
    try {
      const params = new URLSearchParams();
      
      if (options.status) params.append('status', options.status);
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.offset) params.append('offset', options.offset.toString());

      const endpoint = `/api/shares/sent${params.toString() ? '?' + params.toString() : ''}`;
      
      console.log(`[CustomerApi] Getting shares sent with params:`, options);
      
      const response = await apiFetch<GetSharesResponse>(endpoint);

      if (!response.success) {
        throw new Error('Failed to get sent shares');
      }

      console.log(`[CustomerApi] Retrieved ${response.data.shares.length} sent shares`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Get shares sent failed:`, error);
      throw error;
    }
  }

  /**
   * Get shares received by current user
   */
  async getSharesReceived(options: {
    status?: 'pending' | 'accepted' | 'revoked';
    limit?: number;
    offset?: number;
  } = {}): Promise<GetSharesResponse> {
    try {
      const params = new URLSearchParams();
      
      if (options.status) params.append('status', options.status);
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.offset) params.append('offset', options.offset.toString());

      const endpoint = `/api/shares/received${params.toString() ? '?' + params.toString() : ''}`;
      
      console.log(`[CustomerApi] Getting shares received with params:`, options);
      
      const response = await apiFetch<GetSharesResponse>(endpoint);

      if (!response.success) {
        throw new Error('Failed to get received shares');
      }

      console.log(`[CustomerApi] Retrieved ${response.data.shares.length} received shares`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Get shares received failed:`, error);
      throw error;
    }
  }

  /**
   * Get pending shares for current user
   */
  async getPendingShares(): Promise<GetSharesResponse> {
    try {
      console.log(`[CustomerApi] Getting pending shares`);
      
      const response = await apiFetch<GetSharesResponse>('/api/shares/pending');

      if (!response.success) {
        throw new Error('Failed to get pending shares');
      }

      console.log(`[CustomerApi] Retrieved ${response.data.shares.length} pending shares`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Get pending shares failed:`, error);
      throw error;
    }
  }

  /**
   * Revoke a share
   */
  async revokeShare(shareId: string): Promise<{ success: boolean; message: string }> {
    try {
      if (!shareId) {
        throw new Error('Share ID is required for revoking');
      }

      console.log(`[CustomerApi] Revoking share: ${shareId}`);
      
      const response = await apiFetch<{ success: boolean; message: string }>(`/api/shares/${shareId}`, {
        method: 'DELETE'
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to revoke share');
      }

      console.log(`[CustomerApi] Share revoked successfully: ${shareId}`);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Revoke share failed for ${shareId}:`, error);
      throw error;
    }
  }

  async sendSupportTicket(ticketData: SupportTicketRequest): Promise<SupportTicketResponse> {
  try {
    if (!ticketData.subject || !ticketData.message || !ticketData.userEmail || !ticketData.userName) {
      throw new Error('All ticket fields are required');
    }

    console.log(`[CustomerApi] Sending support ticket for: ${ticketData.userEmail}`);
    
    const response = await apiFetch<SupportTicketResponse>('/api/support/send-ticket', {
      method: 'POST',
      body: JSON.stringify(ticketData)
    });

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to send support ticket');
    }

    console.log(`[CustomerApi] Support ticket sent successfully: ${response.data.ticketId}`);
    return response;
  } catch (error) {
    console.error(`[CustomerApi] Send support ticket failed:`, error);
    throw error;
  }
  }

  async sendFeedback(feedbackData: FeedbackRequest): Promise<FeedbackResponse> {
    try {
      if (!feedbackData.message || !feedbackData.userEmail || !feedbackData.userName) {
        throw new Error('All feedback fields are required');
      }

      console.log(`[CustomerApi] Sending feedback email...`);

      const response = await apiFetch<FeedbackResponse>('/api/support/send-feedback', {
        method: 'POST',
        body: JSON.stringify({
          ...feedbackData,
          subject: 'Feedback'
        })
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to send feedback');
      }

      console.log('[CustomerApi] Feedback sent successfully');
      return response;
    } catch (error) {
      console.error('[CustomerApi] Send feedback failed:', error);
      throw error;
    }
  }

  async sendTeamEmail(emailData: TeamEmailRequest): Promise<TeamEmailResponse> {
    if (!emailData.to || !emailData.subject || !emailData.message) {
      throw new Error('Recipient, subject, and email body are required');
    }

    return apiFetch<TeamEmailResponse>('/api/support/send-team-email', {
      method: 'POST',
      body: JSON.stringify(emailData),
    });
  }

  async sendEnterpriseInquiry(inquiryData: EnterpriseInquiryRequest): Promise<EnterpriseInquiryResponse> {
    try {
      if (!inquiryData.name || !inquiryData.email || !inquiryData.company || !inquiryData.message) {
        throw new Error('All inquiry fields are required');
      }

      console.log(`[CustomerApi] Sending enterprise inquiry for: ${inquiryData.company}`);
      
      // Note: This is a public endpoint, so we don't include auth
      const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/support/enterprise-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(inquiryData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.error || errorJson.message || `HTTP ${response.status}: ${response.statusText}`);
        } catch (parseError) {
          throw new Error(`API call failed [${response.status}]: ${errorText.substring(0, 200)}`);
        }
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to submit enterprise inquiry');
      }

      console.log(`[CustomerApi] Enterprise inquiry sent successfully: ${data.data?.inquiryId}`);
      return data;
    } catch (error) {
      console.error(`[CustomerApi] Send enterprise inquiry failed:`, error);
      throw error;
    }
  }

  async reconstructionUploadCredentials(requestId: string): Promise<{
    success: boolean;
    credentials: {
      AccessKeyId: string;
      SecretAccessKey: string;
      SessionToken: string;
      Expiration?: string;
    };
    bucket: string;
    region: string;
    prefix: string;
  }> {
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/support/reconstruction-upload-credentials`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requestId }),
    });
    if (!response.ok) throw new Error(`Upload credentials request failed [${response.status}]`);
    return response.json();
  }

  async reconstructionAgentChat(messages: Array<{ role: 'user' | 'assistant'; content: string }>): Promise<{
    success: boolean;
    reply: string;
    intake?: {
      sceneName: string;
      email: string;
      datasetLink: string;
      sceneType: string;
      industryUseCase: string;
      cameraType: string;
      gpsAvailable: boolean;
      advancedMetadata?: Record<string, unknown>;
      outputPreference?: { type: 'download' | 'viewer'; format?: string };
    };
    worldMapAddress?: { address: string; sceneName: string; description: string };
  }> {
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/support/reconstruction-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages }),
    });
    if (!response.ok) throw new Error(`Chat request failed [${response.status}]`);
    return response.json();
  }

  async reconstructionSubmit(intake: {
    sceneName: string;
    email: string;
    datasetLink: string;
    sceneType: string;
    industryUseCase: string;
    cameraType: string;
    gpsAvailable: boolean;
    advancedMetadata?: Record<string, unknown>;
    outputPreference?: { type: 'download' | 'viewer'; format?: string };
  }, uploadRequestId?: string): Promise<{ success: boolean; requestId: string; title: string; message: string; phase1Failed?: boolean }> {
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/support/reconstruction-submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ intake, ...(uploadRequestId ? { uploadRequestId } : {}) }),
    });
    if (!response.ok) throw new Error(`Submission failed [${response.status}]`);
    return response.json();
  }

  async reconstructionStatus(requestId: string): Promise<{
    success: boolean;
    pending: boolean;
    requestStatus?: string;
    checkStatus?: string;
    warnings?: string[];
    message?: string;
    progressMessages?: string[];
  }> {
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/support/reconstruction-status/${encodeURIComponent(requestId)}`);
    if (!response.ok) throw new Error(`Status check failed [${response.status}]`);
    return response.json();
  }

  async reconstructionProceed(requestId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/support/reconstruction-proceed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requestId }),
    });
    if (!response.ok) throw new Error(`Proceed failed [${response.status}]`);
    return response.json();
  }

  async reconstructionRevise(requestId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/support/reconstruction-revise`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requestId }),
    });
    if (!response.ok) throw new Error(`Revise failed [${response.status}]`);
    return response.json();
  }

  async reconstructionRelay(message: string): Promise<{
    success: boolean;
    reply: string;
    files?: string[];
    done?: boolean;
    warnings?: string[];
  }> {
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/support/reconstruction-relay`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message }),
    });
    if (!response.ok) throw new Error(`Relay request failed [${response.status}]`);
    return response.json();
  }

  async reconstructionWorldMapSubmit(worldMapIntake: {
    address: string;
    sceneName: string;
    email: string;
  }): Promise<{
    success: boolean;
    centerLat?: number;
    centerLon?: number;
    radiusM?: number;
    placeName?: string;
    country?: string;
    wkt?: string;
    message: string;
  }> {
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/support/reconstruction-world-map-submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ worldMapIntake }),
    });
    if (!response.ok) throw new Error(`World map submit failed [${response.status}]`);
    return response.json();
  }

  /**
   * Verify Vexcel imagery covers a chosen extent. No email, no side effects —
   * the answer is what decides whether we ask the user for anything further.
   * Echo the returned radiusM to /proceed so both calls describe one rectangle.
   */
  async reconstructionWorldMapCoverage(body: {
    centerLat: number;
    centerLon: number;
    radiusM: number;
    /** From the geocode step — names the country when coverage is missing. */
    country?: string;
  }): Promise<{
    success: boolean;
    /** Which server-side flow answered: 'preview' adds imagery validation. */
    mode?: 'legacy' | 'preview';
    /** Present on a preview failure: no-coverage | unsuitable | service-error. */
    reason?: string;
    collection?: string;
    year?: string;
    gsd?: string;
    /** Percentage of the chosen AOI the imagery covers, 0-100. Preview mode. */
    coveragePct?: number;
    radiusM?: number;
    wkt?: string;
    /** Preview mode only — grants the image proxy this collection and extent. */
    imageToken?: string;
    views?: string[];
    message: string;
  }> {
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/support/reconstruction-world-map-coverage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`World map coverage check failed [${response.status}]`);
    return response.json();
  }

  /**
   * Proxy URL for one preview image. Loaded by the browser, not by us — the
   * Vexcel key stays server-side and the token pins the request to a single
   * collection and extent.
   */
  worldMapImageUrl(token: string, view: string): string {
    const qs = new URLSearchParams({ t: token, view });
    return `${CUSTOMER_API_BASE_URL}/api/support/reconstruction-world-map-image?${qs}`;
  }

  /** Why the user backed out after seeing the previews. Logged server-side. */
  async reconstructionWorldMapCancel(body: {
    reason: string;
    address?: string;
    collection?: string;
  }): Promise<{ success: boolean }> {
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/support/reconstruction-world-map-cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) return { success: false };
    return response.json();
  }

  async reconstructionWorldMapProceed(body: {
    worldMapIntake: { address: string; sceneName: string; email: string };
    centerLat: number;
    centerLon: number;
    radiusM: number;
    year?: string;
    collection?: string;
    wkt?: string;
  }): Promise<{ success: boolean; requestId?: string; title?: string; message: string }> {
    const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/support/reconstruction-world-map-proceed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`World map proceed failed [${response.status}]`);
    return response.json();
  }

  // ===== RECONSTRUCTION MONITORING METHOD =====

  /**
   * Run monitoring cycle for reconstruction status checks
   * Can monitor all processing scans or specific scans provided
   */
  async runMonitoringCycle(request?: MonitoringCycleRequest): Promise<MonitoringCycleResponse> {
    try {
      console.log(`[CustomerApi] Running monitoring cycle`, {
        scansProvided: request?.scans?.length || 'all eligible scans'
      });
      
      const response = await apiFetch<MonitoringCycleResponse>('/api/reconstructions/monitoring/run-cycle', {
        method: 'POST',
        body: JSON.stringify(request || {})
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to run monitoring cycle');
      }

      console.log(`[CustomerApi] Monitoring cycle completed:`, response.data);
      return response;
    } catch (error) {
      console.error(`[CustomerApi] Run monitoring cycle failed:`, error);
      throw error;
    }
  }

}

// Export singleton instance
export const customerApi = new CustomerApiService();
export default customerApi; 
